import { useTranslation } from "react-i18next";
import {
  AppBar,
  useFlags,
  useSnackbarContext,
  writeToClipboard,
} from "@inductiveautomation/ws-components";
import { useNavigate } from "react-router";
import { useAuth0 } from "@inductiveautomation/ws-components";
import { CurrentUserInformation } from "../../../hooks/currentUser";
import {
  retrieveAuthChannel,
  CHANNEL_MESSAGE_TYPES,
} from "../../../utilities/broadcastChannel";
import { AccountFlags } from "../../../featureFlags.ts";

const PublicAppBar = ({ userInfo }: { userInfo: CurrentUserInformation }) => {
  const { t } = useTranslation();
  const { addSnackbar } = useSnackbarContext();
  const navigate = useNavigate();
  const { loginWithRedirect, logout } = useAuth0();
  const { tsAccountWorkspaceVisibilityFe } = useFlags<AccountFlags>();
  const { sendAuthChannelMessage } = retrieveAuthChannel();
  const onManageAccount = () => {
    if (window.location.pathname === "/") {
      window.location.reload();
    } else {
      void navigate("/");
    }
  };

  const onSignOut = () => {
    void logout({
      logoutParams: {
        returnTo: window._acctConfig?.AUTH0_LOGOUT_URL,
      },
    }).then(() => {
      sendAuthChannelMessage(CHANNEL_MESSAGE_TYPES.LOGOUT);
    });
  };

  return (
    <AppBar
      title={[
        {
          id: "public-profile",
          testId: "public-profile",
          label: t("public_profile.title"),
          onClick: () => undefined,
          actionLists: [
            [
              {
                id: "copy-link",
                testId: "copy-link",
                label: t("public_profile.actions.copy_link.label"),
                iconLeft: "link",
                onClick: () => {
                  writeToClipboard(window.location.href)
                    .then(() => {
                      addSnackbar(
                        t("public_profile.actions.copy_link.success_message")
                      );
                    })
                    .catch((error: unknown) => {
                      addSnackbar(
                        t("public_profile.actions.copy_link.error_message"),
                        { type: "error" }
                      );
                      console.error(error);
                    });
                },
              },
            ],
          ],
        },
      ]}
      user={
        userInfo.user && !userInfo.isLoading
          ? {
              ...userInfo.user,
              publicProfileEnabled: true,
            }
          : undefined
      }
      loading={userInfo.isLoading}
      onNavigationMenuClick={() => undefined}
      workspacesUrl=""
      workspaceFeatureFlag={tsAccountWorkspaceVisibilityFe}
      actions={{
        onManageAccount,
        onViewProfile: () => window.location.reload(),
        onSignIn: () => {
          void loginWithRedirect({
            appState: { returnTo: window.location.pathname },
          }).then(() => {
            sendAuthChannelMessage(CHANNEL_MESSAGE_TYPES.LOGIN);
          });
        },
        onSignOut,
      }}
      translations={{
        accountTooltip: t("app_bar.account_tooltip"),
        appServicesTooltip: t("app_bar.apps_services_tooltip"),
        signIn: t("app_bar.sign_in"),
        services: {
          community_forums: t("app_bar.services.community_forums"),
          customer_feedback: t("app_bar.services.customer_feedback"),
          inductive_automation: t("app_bar.services.inductive_automation"),
          inductive_university: t("app_bar.services.inductive_university"),
          integrator_account: t("app_bar.services.integrator_account"),
          ignition_exchange: t("app_bar.services.ignition_exchange"),
          workspaces: t("app_bar.services.workspaces"),
        },
        account: {
          manageAccount: t("app_bar.manage_account"),
          viewProfile: t("app_bar.view_profile"),
          helpCenter: t("app_bar.help_center"),
          signOut: t("app_bar.sign_out"),
        },
      }}
      publicFacing
    />
  );
};

export default PublicAppBar;
